import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Loader2, SendHorizonal } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "mt-1 w-full bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50";

const FILTERS = [
  { value: "overdue_balance", label: "Returned, but still owe money" },
  { value: "active_now", label: "Currently renting (not yet returned)" },
  { value: "all_customers", label: "Everyone who has booked with us" },
];

/** Message a filtered group of customers at once (e.g. everyone with an overdue balance).
 * Each recipient gets their own message thread, same as a one-off composed message. */
export default function BulkMessage({ token, onDone }) {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState(FILTERS[0].value);
  const [audience, setAudience] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState("");
  const [job, setJob] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/contact/bulk-audience`, { ...H, params: { filter } })
      .then(({ data }) => setAudience(data)).catch(() => setAudience(null));
    // eslint-disable-next-line
  }, [open, filter]);

  useEffect(() => {
    if (!job || job.status === "done") return;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/contact/bulk-compose/${job.job_id || job.id}`, H);
        setJob({ ...data, job_id: job.job_id || job.id });
        if (data.status === "done") { toast.success(`Done — sent ${data.sent}, failed ${data.failed} of ${data.total}.`); onDone?.(); }
      } catch { /* keep last known state */ }
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [job]);

  const send = async (method) => {
    if (!message.trim()) return toast.error("Type a message first.");
    if (!audience?.count) return toast.error("No one matches this group.");
    if (!window.confirm(`This will message ${audience.count} ${audience.count === 1 ? "person" : "people"} via ${method === "both" ? "text + email" : method}. Continue?`)) return;
    setSending(method);
    try {
      const { data } = await axios.post(`${API}/contact/bulk-compose`,
        { audience_filter: filter, message, method }, H);
      setJob({ ...data, sent: 0, failed: 0, status: "running" });
      toast.success(`Started — sending to ${data.total} people…`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the bulk send.");
    } finally {
      setSending("");
    }
  };

  const reset = () => { setMessage(""); setJob(null); setOpen(false); };

  return (
    <>
      <button data-testid="bulk-message-btn" onClick={() => setOpen(true)}
        className="flex items-center gap-2 text-sm border border-white/20 text-white/75 font-bold px-4 py-2 rounded-md hover:border-[#F2B705] hover:text-[#F2B705] transition-colors">
        <Users size={16} /> Bulk Message
      </button>

      <Dialog open={open} onOpenChange={(v) => !v && reset()}>
        <DialogContent data-testid="bulk-message-dialog" className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display"><Users size={18} className="text-[#F2B705]" /> Bulk Message</DialogTitle>
            <DialogDescription className="text-white/45">Send the same text or email to everyone in a group at once. Each person gets their own message thread.</DialogDescription>
          </DialogHeader>

          {job ? (
            <div className="space-y-3">
              <div className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4" data-testid="bulk-job-progress">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-white/70">{job.status === "done" ? "Finished" : "Sending…"}</span>
                  <span className="text-white/50">{job.sent + job.failed} / {job.total}</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-[#F2B705] transition-all" style={{ width: `${job.total ? ((job.sent + job.failed) / job.total) * 100 : 0}%` }} />
                </div>
                <div className="flex gap-4 mt-2 text-xs">
                  <span className="text-green-400">✓ {job.sent} sent</span>
                  {job.failed > 0 && <span className="text-red-400">✗ {job.failed} failed</span>}
                </div>
              </div>
              <button data-testid="bulk-job-close" onClick={reset}
                className="w-full bg-white/10 text-white font-bold py-2.5 rounded-md hover:bg-white/15">
                {job.status === "done" ? "Close" : "Close (keeps sending in background)"}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/45">Who to message</label>
                <select data-testid="bulk-audience-filter" className={inputCls} value={filter} onChange={(e) => setFilter(e.target.value)}>
                  {FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
                <p data-testid="bulk-audience-count" className="text-xs text-white/40 mt-1.5">
                  {audience === null ? "Checking…" : audience.count === 0 ? "No one currently matches this group." :
                    `This will message ${audience.count} ${audience.count === 1 ? "person" : "people"}${audience.sample?.length ? ` (e.g. ${audience.sample.slice(0, 3).join(", ")}${audience.count > 3 ? "…" : ""})` : ""}.`}
                </p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/45">Message</label>
                <textarea data-testid="bulk-message-input" rows={4} className={inputCls} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="What do you want to say to this group?" />
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <button data-testid="bulk-send-sms" onClick={() => send("sms")} disabled={!!sending || !audience?.count}
                  className="flex items-center gap-1.5 text-sm font-bold bg-[#F2B705] text-black px-4 py-2 rounded-md hover:bg-white disabled:opacity-50">
                  {sending === "sms" ? <Loader2 size={14} className="animate-spin" /> : <SendHorizonal size={14} />} Send by Text
                </button>
                <button data-testid="bulk-send-email" onClick={() => send("email")} disabled={!!sending || !audience?.count}
                  className="flex items-center gap-1.5 text-sm font-bold border border-white/20 text-white/75 px-4 py-2 rounded-md hover:border-[#F2B705] hover:text-[#F2B705] disabled:opacity-50">
                  {sending === "email" ? <Loader2 size={14} className="animate-spin" /> : <SendHorizonal size={14} />} Send by Email
                </button>
                <button data-testid="bulk-send-both" onClick={() => send("both")} disabled={!!sending || !audience?.count}
                  className="flex items-center gap-1.5 text-sm font-bold border border-white/20 text-white/55 px-4 py-2 rounded-md hover:border-[#F2B705] hover:text-[#F2B705] disabled:opacity-50">
                  {sending === "both" ? <Loader2 size={14} className="animate-spin" /> : <SendHorizonal size={14} />} Send Both
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
